"use client";

import { useCallback, useEffect, useMemo, useState } from "react";
import { z } from "zod";

type Serializer<T> = (value: T) => string;
type Deserializer<T> = (value: string) => T;

// Per-key in-memory subscriber registry for same-tab synchronization
const subscribers = new Map<string, Set<(serialized: string | null) => void>>();

function getSubscribers(key: string) {
  let set = subscribers.get(key);
  if (!set) {
    set = new Set<(serialized: string | null) => void>();
    subscribers.set(key, set);
  }
  return set;
}

function safeParse<T>(
  raw: string | null,
  fallback: T,
  deserialize: Deserializer<T>,
): T {
  if (raw == null) return fallback;
  try {
    return deserialize(raw);
  } catch {
    return fallback;
  }
}

const defaultSerialize = <T>(value: T) =>
  z.string().parse(JSON.stringify(value));

export function useLocalStorage<T>(
  key: `scoopulator:${string}`,
  initialValue: T,
  options: {
    schema: z.ZodType<T>;
    serialize?: Serializer<T>;
    deserialize?: Deserializer<T>;
  },
) {
  const {
    schema,
    serialize: customSerialize,
    deserialize: customDeserialize,
  } = options;
  const serialize = customSerialize ?? defaultSerialize;
  const deserialize = useCallback(
    (value: string) =>
      customDeserialize
        ? customDeserialize(value)
        : schema.parse(JSON.parse(value)),
    [customDeserialize, schema],
  );

  const initialResolved = useMemo(() => initialValue, [initialValue]);

  const [state, setState] = useState({
    value: initialResolved,
    shouldWrite: false,
  });

  // Hydrate from localStorage after mount to avoid SSR/CSR mismatch
  useEffect(() => {
    if (!globalThis.window) return;
    try {
      const raw = window.localStorage.getItem(key);
      const next = safeParse(raw, initialResolved, deserialize);
      setState({ value: next, shouldWrite: false });
    } catch {
      // Pass
    }
  }, [key, deserialize, initialResolved]);

  // Only user-initiated updates are persisted. Hydration and synchronization
  // updates are deliberately marked as read-only to avoid feedback loops.
  useEffect(() => {
    if (!globalThis.window) return;
    if (!state.shouldWrite) return;
    let serialized: string;
    try {
      serialized = serialize(state.value);
      window.localStorage.setItem(key, serialized);
    } catch {
      return;
    }
    const subs = getSubscribers(key);
    subs.forEach((fn) => fn(serialized));
  }, [key, state, serialize]);

  useEffect(() => {
    const subs = getSubscribers(key);
    const handle = (serialized: string | null) => {
      const value = safeParse(serialized, initialResolved, deserialize);
      setState({ value, shouldWrite: false });
    };
    subs.add(handle);
    return () => {
      subs.delete(handle);
      if (subs.size === 0) subscribers.delete(key);
    };
  }, [key, deserialize, initialResolved]);

  useEffect(() => {
    if (!globalThis.window) return;
    const onStorage = (e: StorageEvent) => {
      if (e.storageArea !== window.localStorage) return;
      if (e.key !== key) return;
      const next = safeParse(e.newValue, initialResolved, deserialize);
      setState({ value: next, shouldWrite: false });
    };
    window.addEventListener("storage", onStorage);
    return () => window.removeEventListener("storage", onStorage);
  }, [key, deserialize, initialResolved]);

  const set = useCallback((value: T) => {
    setState({ value, shouldWrite: true });
  }, []);

  const remove = useCallback(() => {
    setState({ value: initialResolved, shouldWrite: false });
    if (globalThis.window) {
      try {
        window.localStorage.removeItem(key);
      } catch {
        // Pass
      }
    }
    const subs = getSubscribers(key);
    subs.forEach((fn) => fn(null));
  }, [key, initialResolved]);

  return [state.value, set, remove] as const;
}
